
// FileName: /AddPurchase.tsx
import { KTSVG } from '../../../_metronic/helpers'
import React, { useEffect, useState, useMemo, forwardRef, useImperativeHandle } from 'react'
import { Modal } from 'react-bootstrap'
import { useAuth } from '../../../app/modules/auth'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import Select from 'react-select'
import { useSelectStyles } from '../../helpers/useSelectStyles'
import dayjs from 'dayjs'
import { useSuppliers, useAllproductsandtypes, useCreatePurchase, useUpdateallproducts } from '../../contextProviders/Atomqueryprovider'
import { Column } from 'react-table'
import { Mainsubtable2 } from '../../tables/Mainsubtable2'
import { useWindowWidth } from '@react-hook/window-size/throttled'

interface PurchaseItem {
  id: string // For table key
  product_id: number | string
  product_name: string
  quantity: number
  unit_cost: number
  total_cost: number
}

interface PurchaseFormValues {
  purchase_number: string
  supplier_id: string | number
  purchase_order_id: string
  amount_paid: number
  payment_terms: string
  due_date: string
  purchase_date: string
  notes: string
}

// Define the structure of the purchase object passed for updating
interface ExistingPurchaseData {
  purchase_id: number;
  purchase_number: string;
  supplier_id: number;
  purchase_order_id: string | null;
  total_amount: number;
  amount_paid: number;
  balance_due: number;
  payment_status: string;
  payment_terms: string;
  due_date: string;
  purchase_date: string;
  received_date: string | null;
  user_id: number;
  notes: string | null;
  status: string;
  items: PurchaseItem[]; // Existing items
  supplier_details: {
    supplier_id: number;
    supplier_name: string;
  };
  user_details: {
    user_id: number;
    user_first_name: string;
  };
}

type Props = {
  show: boolean
  handleClose: () => void
  purchaseToUpdate?: ExistingPurchaseData | {} // Optional prop for existing purchase
  mode?: 'new' | 'add' // 'new' for new purchase, 'add' for adding items to existing
}

const CURRENCY_APP = process.env.REACT_APP_CURRENCY || 'KES'

const truncate = require('truncate')

const addCommas = (num: any) => {
  // Handle undefined, null, or non-numeric values
  if (num === undefined || num === null || num === '') {
    return '0'
  }

  // Convert to number if it's a string
  const numValue = typeof num === 'string' ? parseFloat(num) : num

  // Check if it's a valid number
  if (isNaN(numValue)) {
    return '0'
  }

  return numValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const generateId = () => Math.random().toString(36).substr(2, 9)

// Function to generate a new purchase number
const generatePurchaseNumber = () => 'PO-' + new Date().valueOf()

// Payment terms options
const paymentTermsOptions = [
  { value: 'Cash on Delivery', label: 'Cash on Delivery' },
  { value: 'Cash on Next Delivery', label: 'Cash on Next Delivery' },
  { value: 'Cash in Advance', label: 'Cash in Advance' },
  { value: 'End of Month', label: 'End of Month' }
]

const getFormData: any = <T extends Record<string, any>>(obj: T): FormData =>
  Object.entries(obj).reduce((formData: FormData, [key, value]) => {
    if (value instanceof FileList) {
      for (let i = 0; i < value.length; i++) {
        formData.append(key, value[i])
      }
    } else {
      formData.append(key, value)
    }
    return formData
  }, new FormData())

export const AddPurchase = forwardRef<any, Props>(({ show, handleClose, purchaseToUpdate = {}, mode = 'new' }, ref) => {
  const [loading, setLoading] = useState(false)
  const [purchaseItems, setPurchaseItems] = useState<PurchaseItem[]>([])
  const [selectedProduct, setSelectedProduct] = useState<any>(null)
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null)
  const [selectedPaymentTerms, setSelectedPaymentTerms] = useState<any>(null)
  const [quantity, setQuantity] = useState<number>(1)
  const [unitCost, setUnitCost] = useState<number>(0)
  const [totalAmount, setTotalAmount] = useState<number>(0)
  const [existingTotalAmount, setExistingTotalAmount] = useState<number>(0) // Track existing purchase total
  const [purchaseData, setPurchaseData] = useState<any>({})
  const [submittingReady, setSubmittingReady] = useState<boolean>(false)
  const [purchaseNumber, setPurchaseNumber] = useState<string>(generatePurchaseNumber())
  const onlyWidth = useWindowWidth()
  const [showUpdatePricing, setShowUpdatePricing] = useState(false)
  const [newBuyingPrice, setNewBuyingPrice] = useState(0)
  const [newSellingPrice, setNewSellingPrice] = useState(0)
  const [loadingUpdate, setLoadingUpdate] = useState(false)
  const [productUpdateData, setProductUpdateData] = useState<any>({})
  const { currentUser } = useAuth()
  const selectStyles = useSelectStyles()

  // Expose functions to parent component via ref
  useImperativeHandle(ref, () => ({
    Resetmodalvalues() {
      resetFormState();
      setPurchaseNumber(generatePurchaseNumber());
      setExistingTotalAmount(0);
    },
    ResetmodalvaluesForAddItems(existingPurchase: ExistingPurchaseData) {
      resetFormState();
      // Pre-fill form with existing purchase data
      formik.setValues({
        purchase_number: existingPurchase.purchase_number,
        supplier_id: existingPurchase.supplier_id,
        purchase_order_id: existingPurchase.purchase_order_id || '',
        amount_paid: existingPurchase.amount_paid,
        payment_terms: existingPurchase.payment_terms,
        due_date: dayjs(existingPurchase.due_date).format('YYYY-MM-DDTHH:mm'),
        purchase_date: dayjs(existingPurchase.purchase_date).format('YYYY-MM-DDTHH:mm'),
        notes: existingPurchase.notes || '',
      });
      setPurchaseNumber(existingPurchase.purchase_number);
      setExistingTotalAmount(existingPurchase.total_amount); // Store existing total
      // Set selected supplier and payment terms for display
      setSelectedSupplier({ value: existingPurchase.supplier_id, label: existingPurchase.supplier_details?.supplier_name || 'N/A' });
      setSelectedPaymentTerms(paymentTermsOptions.find(opt => opt.value === existingPurchase.payment_terms));
      // Load existing items into the table
      setPurchaseItems(existingPurchase.items.map(item => ({
        id: generateId(), // Generate new ID for UI
        product_id: item.product_id,
        product_name: item.product_name || 'N/A',
        quantity: item.quantity,
        unit_cost: item.unit_cost,
        total_cost: item.total_cost,
      })));
    }
  }));

  // Helper to reset all local state
  const resetFormState = () => {
    formik.resetForm();
    setPurchaseItems([]);
    setSelectedProduct(null);
    setSelectedSupplier(null);
    setSelectedPaymentTerms(null);
    setQuantity(1);
    setUnitCost(0);
    setTotalAmount(0);
    setPurchaseData({});
    setSubmittingReady(false);
    setShowUpdatePricing(false);
    setNewBuyingPrice(0);
    setNewSellingPrice(0);
  };

  // Fetch data
  const { data: suppliersData, isLoading: suppliersLoading } = useSuppliers(
    Number(currentUser?.user_id),
    0,
    10000,
    'supplier_name',
    'asc',
    ''
  )

  const {
    isLoading: allproductsandtypesloading,
    data: allproductsandtypesdata,
    refetch: allproductsandtypesrefetch,
  } = useAllproductsandtypes()

  const suppliers = Array.isArray(suppliersData?.data?.suppliers)
    ? (suppliersData?.data?.suppliers ?? []).map((s: any) => ({
      value: s.supplier_id,
      label: s.supplier_name,
    }))
    : []

  const products =
    allproductsandtypesdata?.data?.map((val: any) => {
      return {
        value: val.product_id,
        label: val.product_name,
        product_name: val.product_name,
        units_in_stock: val.units_in_stock,
        unit_buying_price: val.unit_buying_price,
        product_category_id: val.product_category_id,
        product_unit_id: val.product_unit_id,
        unit_selling_price: val.unit_selling_price,
        unit_name: val.unit_name,
        category_name: val.category_name,
        discount_percentage: val.discount_percentage || 0,
        reorder_level: val.reorder_level || 0,
        product_description: val.product_description || '',
        product_division: val.product_division || 1,
        product_image: val.product_image || '',
      }
    }) || []

  const {
    mutateAsync: createPurchaseMutateAsync
  } = useCreatePurchase(purchaseData)

  const { mutateAsync: updateProductAsync } = useUpdateallproducts(getFormData(productUpdateData))

  const initialValues: PurchaseFormValues = useMemo(() => {
    if (mode === 'add' && (purchaseToUpdate as ExistingPurchaseData)?.purchase_id) {
      const existingPurchase = purchaseToUpdate as ExistingPurchaseData;
      return {
        purchase_number: existingPurchase.purchase_number,
        supplier_id: existingPurchase.supplier_id,
        purchase_order_id: existingPurchase.purchase_order_id || '',
        amount_paid: existingPurchase.amount_paid,
        payment_terms: existingPurchase.payment_terms,
        due_date: dayjs(existingPurchase.due_date).format('YYYY-MM-DDTHH:mm'),
        purchase_date: dayjs(existingPurchase.purchase_date).format('YYYY-MM-DDTHH:mm'),
        notes: existingPurchase.notes || '',
      };
    }
    return {
      purchase_number: generatePurchaseNumber(),
      supplier_id: '',
      purchase_order_id: '',
      amount_paid: 0,
      payment_terms: 'Cash on Delivery', // Default value
      due_date: dayjs().add(7, 'day').format('YYYY-MM-DDTHH:mm'), // Default due date 7 days from now
      purchase_date: dayjs().format('YYYY-MM-DDTHH:mm'), // Updated to include time
      notes: '',
    };
  }, [mode, purchaseToUpdate]);

  const purchaseSchema = Yup.object().shape({
    purchase_number: Yup.string().required('Purchase number is required'),
    supplier_id: Yup.number().required('Supplier is required'),
    purchase_order_id: Yup.string(),
    amount_paid: Yup.number().min(0),
    payment_terms: Yup.string().required('Payment terms are required'),
    due_date: Yup.string().required('Due date is required'), // Changed to string for datetime-local
    purchase_date: Yup.string().required('Purchase date is required'), // Changed to string for datetime-local
    notes: Yup.string(),
  })

  const formik = useFormik<PurchaseFormValues>({
    initialValues,
    validationSchema: purchaseSchema,
    enableReinitialize: true,
    // Fixed onSubmit function in AddPurchase.tsx
    onSubmit: async (values, { setStatus, setSubmitting, resetForm }) => {
      console.log('Form values on submit:', values)
      console.log('Purchase items on submit:', purchaseItems)

      if (purchaseItems.length === 0) {
        setStatus('Please add at least one item to the purchase')
        setSubmitting(false)
        return
      }

      setLoading(true)

      // Calculate new items total (items added in this session)
      const newItemsTotal = purchaseItems.filter(item => !item.id.startsWith('existing_')).reduce((sum, item) => sum + item.total_cost, 0)

      // For add mode, total amount should be existing + new items
      // For new mode, total amount is just the items total
      const finalTotalAmount = mode === 'add' ? existingTotalAmount + newItemsTotal : totalAmount
      const balance_due = finalTotalAmount - values.amount_paid

      // Build the base payload with items
      let payload: any = {
        user_id: currentUser?.user_id,
        items: purchaseItems.map((item) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          unit_cost: item.unit_cost,
          total_cost: item.total_cost,
        })),
      };

      if (mode === 'add' && (purchaseToUpdate as any)?.purchase_id) {
        // ADDING ITEMS TO EXISTING PURCHASE
        // Include purchase_id to indicate this is an update/add operation
        payload.purchase_id = (purchaseToUpdate as any).purchase_id;

        // For existing purchases, only include fields that might change
        // Don't include purchase_number as it shouldn't change
        payload.supplier_id = Number(values.supplier_id);
        payload.total_amount = finalTotalAmount;
        payload.amount_paid = values.amount_paid;
        payload.balance_due = balance_due;
        payload.payment_terms = values.payment_terms;
        payload.due_date = values.due_date;
        payload.purchase_date = values.purchase_date;
        payload.notes = values.notes;
        payload.purchase_order_id = values.purchase_order_id;
      } else {
        // CREATING NEW PURCHASE
        // Do NOT include purchase_id (or set it to null/undefined)
        // Include all form values including purchase_number
        payload = {
          ...payload,
          purchase_number: values.purchase_number,
          supplier_id: Number(values.supplier_id),
          total_amount: finalTotalAmount,
          amount_paid: values.amount_paid,
          balance_due: balance_due,
          payment_terms: values.payment_terms,
          due_date: values.due_date,
          purchase_date: values.purchase_date,
          notes: values.notes,
          purchase_order_id: values.purchase_order_id,
        };
      }

      console.log('Payload being sent:', payload);
      setPurchaseData(payload)
      setSubmittingReady(true)
    },
  })

  // Effect to handle the actual submission after state is set
  useEffect(() => {
    if (submittingReady && purchaseData && Object.keys(purchaseData).length > 0) {
      console.log('Sending purchase data:', purchaseData)

      createPurchaseMutateAsync(purchaseData, {
        onSuccess: () => {
          setLoading(false)
          resetFormState(); // Use the helper function
          setPurchaseNumber(generatePurchaseNumber()); // Generate new number for next new purchase
          setExistingTotalAmount(0); // Reset existing total
          setSubmittingReady(false);
          handleClose();
        },
        onError: (error) => {
          console.error('Error creating/updating purchase:', error)
          formik.setStatus('Error creating/updating purchase. Please try again.')
          formik.setSubmitting(false)
          setLoading(false)
          setSubmittingReady(false)
        },
      })
    }
  }, [submittingReady, purchaseData])

  // Calculate total amount when items change
  useEffect(() => {
    if (mode === 'add') {
      // For add mode, only calculate total of new items (not including existing items)
      const newItemsTotal = purchaseItems.filter(item => !item.id.startsWith('existing_')).reduce((sum, item) => sum + item.total_cost, 0)
      setTotalAmount(newItemsTotal)
    } else {
      // For new mode, calculate total of all items
      const total = purchaseItems.reduce((sum, item) => sum + item.total_cost, 0)
      setTotalAmount(total)
    }
  }, [purchaseItems, mode])

  // Reset form when modal closes
  useEffect(() => {
    if (!show) {
      resetFormState(); // Use the helper function
      setPurchaseNumber(generatePurchaseNumber()); // Generate new number for next new purchase
      setExistingTotalAmount(0); // Reset existing total
    }
  }, [show])

  // Update formik values when purchase number changes (only for new mode)
  useEffect(() => {
    if (mode === 'new') {
      formik.setFieldValue('purchase_number', purchaseNumber)
    }
  }, [purchaseNumber, mode])

  // Sync selectedSupplier with formik value
  useEffect(() => {
    if (formik.values.supplier_id && suppliers.length > 0) {
      const supplier = suppliers.find((s: any) => s.value === formik.values.supplier_id)
      if (supplier && (!selectedSupplier || selectedSupplier.value !== supplier.value)) {
        setSelectedSupplier(supplier)
      }
    }
  }, [formik.values.supplier_id, suppliers])

  // Sync selectedPaymentTerms with formik value
  useEffect(() => {
    if (formik.values.payment_terms) {
      const paymentTerm = paymentTermsOptions.find((option) => option.value === formik.values.payment_terms)
      if (paymentTerm && (!selectedPaymentTerms || selectedPaymentTerms.value !== paymentTerm.value)) {
        setSelectedPaymentTerms(paymentTerm)
      }
    }
  }, [formik.values.payment_terms])

  const handleSupplierChange = (selectedOption: any) => {
    setSelectedSupplier(selectedOption)
    formik.setFieldValue('supplier_id', selectedOption ? selectedOption.value : '')
  }

  const handlePaymentTermsChange = (selectedOption: any) => {
    setSelectedPaymentTerms(selectedOption)
    formik.setFieldValue('payment_terms', selectedOption ? selectedOption.value : '')
  }

  const handleProductChange = (selectedOption: any) => {
    setSelectedProduct(selectedOption)
    setQuantity(1)
    setUnitCost(selectedOption ? selectedOption.unit_buying_price || 0 : 0)
    setShowUpdatePricing(false)
  }

  const handleAddItem = () => {
    if (!selectedProduct || quantity <= 0 || unitCost < 0) return // Allow zero unit cost

    const newItem: PurchaseItem = {
      id: generateId(),
      product_id: selectedProduct.value,
      product_name: selectedProduct.label,
      quantity: quantity,
      unit_cost: unitCost,
      total_cost: quantity * unitCost,
    }

    setPurchaseItems([...purchaseItems, newItem])
    // Only reset product selection, not supplier
    setSelectedProduct(null)
    setQuantity(1)
    setUnitCost(0)
  }

  const handleRemoveItem = (index: number) => {
    setPurchaseItems(purchaseItems.filter((_, i) => i !== index))
  }

  const handleRemoveAllItems = () => {
    setPurchaseItems([])
  }

  const handleShowUpdatePricing = () => {
    if (selectedProduct) {
      setNewBuyingPrice(selectedProduct.unit_buying_price || 0)
      setNewSellingPrice(selectedProduct.unit_selling_price || 0)
      setShowUpdatePricing(true)
    }
  }

  const handleUpdatePrices = async () => {
    if (!selectedProduct) return

    setLoadingUpdate(true)

    setProductUpdateData
      ({
        product_image: selectedProduct.product_image,
        user_id: currentUser?.user_id,
        discount_percentage: selectedProduct.discount_percentage,
        product_name: selectedProduct.product_name,
        product_id: selectedProduct.value,
        units_in_stock: selectedProduct.units_in_stock,
        unit_buying_price: newBuyingPrice,
        reorder_level: selectedProduct.reorder_level,
        product_category_id: selectedProduct.product_category_id,
        product_unit_id: selectedProduct.product_unit_id,
        unit_selling_price: newSellingPrice,
        product_description: selectedProduct.product_description,
        product_division: selectedProduct.product_division,
        product_file: '',
      })


  }



  useEffect(() => {
    if (loadingUpdate && productUpdateData) {


      try {
        updateProductAsync(getFormData(productUpdateData))
        allproductsandtypesrefetch()
        setUnitCost(newBuyingPrice)
        setShowUpdatePricing(false)
      } catch (error) {
        console.error('Error updating product prices:', error)
        // Optionally set an error message
      } finally {
        setLoadingUpdate(false)
      }
    }
  }, [loadingUpdate])

  // Get the final total amount to display
  const getFinalTotalAmount = () => {
    if (mode === 'add') {
      return existingTotalAmount + totalAmount
    }
    return totalAmount
  }

  // Table columns for added items
  const itemColumns: ReadonlyArray<
    Column<{
      product_name: string
      quantity: number
      unit_cost: number
      total_cost: number
    }>
  > = [
      {
        Header: 'Product',
        accessor: 'product_name',
        Cell: ({ cell: { value } }) => (
          <span className='text-dark text-nowrap fw-bolder d-block mb-1 fs-6' title={value}>
            {truncate(value, 20)}
          </span>
        ),
      },
      {
        Header: 'Quantity',
        accessor: 'quantity',
        Cell: ({ cell: { value } }) => (
          <span className='text-dark text-nowrap fw-bolder d-block mb-1 fs-6'>{value}</span>
        ),
      },
      {
        Header: 'Unit Cost',
        accessor: 'unit_cost',
        Cell: ({ cell: { value } }) => (
          <span className='text-dark text-nowrap fw-bolder d-block mb-1 fs-6'>
            {addCommas(Number(value).toFixed(2))} {CURRENCY_APP}
          </span>
        ),
      },
      {
        Header: 'Total',
        accessor: 'total_cost',
        Cell: ({ cell: { value } }) => (
          <span className='text-dark text-nowrap fw-bolder d-block mb-1 fs-6'>
            {addCommas(Number(value).toFixed(2))} {CURRENCY_APP}
          </span>
        ),
      },
      {
        Header: () => null,
        id: 'expander',
        Cell: ({ row }: any) => (
          <div className='d-flex justify-content-end flex-shrink-0'>
            <button
              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm border'
              onClick={(e) => {
                e.preventDefault()
                handleRemoveItem(row.index)
              }}
            >
              <span className='svg-icon svg-icon-3'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width={24}
                  height={24}
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <path
                    d='M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z'
                    fill='#f1416c'
                  />
                  <path
                    opacity='0.5'
                    d='M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z'
                    fill='#f1416c'
                  />
                  <path
                    opacity='0.5'
                    d='M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z'
                    fill='#f1416c'
                  />
                </svg>
              </span>
            </button>
          </div>
        ),
      },
    ]

  // Mobile-friendly columns
  const itemColumnsMobile: ReadonlyArray<
    Column<{
      product_name: string
      quantity: number
      unit_cost: number
      total_cost: number
    }>
  > = [
      {
        Header: 'Item Details',
        accessor: 'product_name',
        Cell: ({ cell: { value }, row }) => (
          <>
            <span className='text-dark text-nowrap fw-bolder d-block mb-1 fs-6'>
              <span title={value}>{truncate(value, 20)}</span>
              <span className='h-20px border-gray-200 border-start mx-2'></span>
              <span>{row.original.quantity}</span>
            </span>
            <span className='text-muted text-nowrap d-block mb-1 fs-6'>
              {addCommas(row.original.total_cost.toFixed(2))} {CURRENCY_APP}
            </span>
          </>
        ),
      },
      {
        Header: () => null,
        id: 'expander',
        Cell: ({ row }: any) => (
          <div className='d-flex justify-content-end flex-shrink-0'>
            <button
              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm border'
              onClick={(e) => {
                e.preventDefault()
                handleRemoveItem(row.index)
              }}
            >
              <span className='svg-icon svg-icon-3'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width={24}
                  height={24}
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <path
                    d='M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z'
                    fill='#f1416c'
                  />
                  <path
                    opacity='0.5'
                    d='M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z'
                    fill='#f1416c'
                  />
                  <path
                    opacity='0.5'
                    d='M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z'
                    fill='#f1416c'
                  />
                </svg>
              </span>
            </button>
          </div>
        ),
      },
    ]

  const tableData = useMemo(() => purchaseItems, [purchaseItems])

  return (
    <Modal
      className='modal fade'
      show={show}
      onHide={handleClose}
      centered
      dialogClassName='modal-lg'
      data-backdrop='static'
      tabIndex={-1}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h3 className='fw-bolder'>{mode === 'add' ? 'Add Items to Purchase' : 'Add Purchase'}</h3>
          <div
            className='btn btn-icon btn-sm btn-active-light-primary ms-2'
            onClick={handleClose}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
          </div>
        </div>

        <form className='form' noValidate onSubmit={formik.handleSubmit}>
          <div className='modal-body'>
            <div className='fv-row fw-bold fs-7 text-gray-400 mb-7'>
              Purchase No <span className='h-20px border-gray-300 border-start mx-4'></span>{' '}
              {purchaseNumber}
              {mode === 'add' && <span className='badge badge-light-info ms-3'>Adding to Existing</span>}
            </div>

            {formik.status && (
              <div className='alert alert-danger mb-7'>
                <div className='alert-text font-weight-bold'>{formik.status}</div>
              </div>
            )}

            {/* Basic Purchase Information */}
            <div className='row mb-7'>
              <div className='col-lg-6 fv-row'>
                <label className='required fw-bold fs-6 mb-2'>Supplier</label>
                <Select
                  styles={{
                    ...selectStyles,
                    control: (provided) => ({
                      ...selectStyles.control?.(provided),
                      backgroundColor: mode === 'add' ? '#d1f2eb' : provided.backgroundColor,
                    })
                  }}
                  options={suppliers}
                  isLoading={suppliersLoading}
                  isSearchable={true}
                  placeholder='Select supplier...'
                  onChange={handleSupplierChange}
                  value={selectedSupplier}
                  onBlur={() => formik.setFieldTouched('supplier_id', true)}
                  isDisabled={mode === 'add'} // Disable supplier selection when adding to existing
                />
                {formik.touched.supplier_id && formik.errors.supplier_id && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.supplier_id}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className='col-lg-6 fv-row'>
                <label className='required fw-bold fs-6 mb-2'>Purchase Date & Time</label>
                <input
                  {...formik.getFieldProps('purchase_date')}
                  type='datetime-local'
                  className={clsx(
                    'form-control form-control-lg form-control-solid',
                    {
                      'is-invalid': formik.touched.purchase_date && formik.errors.purchase_date,
                    },
                    {
                      'is-valid': formik.touched.purchase_date && !formik.errors.purchase_date,
                    }
                  )}
                  style={{
                    backgroundColor: mode === 'add' ? '#d1f2eb' : undefined
                  }}
                  disabled={mode === 'add'} // Disable when adding to existing
                />
                {formik.touched.purchase_date && formik.errors.purchase_date && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.purchase_date}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-7'>
              <div className='col-lg-6 fv-row'>
                <label className='fw-bold fs-6 mb-2'>Purchase Order ID</label>
                <input
                  {...formik.getFieldProps('purchase_order_id')}
                  type='text'
                  className={clsx(
                    'form-control form-control-lg form-control-solid',
                    {
                      'is-invalid':
                        formik.touched.purchase_order_id && formik.errors.purchase_order_id,
                    },
                    {
                      'is-valid':
                        formik.touched.purchase_order_id && !formik.errors.purchase_order_id,
                    }
                  )}
                  style={{
                    backgroundColor: mode === 'add' ? '#d1f2eb' : undefined
                  }}
                  placeholder='Optional reference number'
                  disabled={mode === 'add'} // Disable when adding to existing
                />
              </div>

              <div className='col-lg-6 fv-row'>
                <label className='required fw-bold fs-6 mb-2'>Due Date & Time</label>
                <input
                  {...formik.getFieldProps('due_date')}
                  type='datetime-local'
                  className={clsx(
                    'form-control form-control-lg form-control-solid',
                    {
                      'is-invalid': formik.touched.due_date && formik.errors.due_date,
                    },
                    {
                      'is-valid': formik.touched.due_date && !formik.errors.due_date,
                    }
                  )}
                  style={{
                    backgroundColor: mode === 'add' ? '#d1f2eb' : undefined
                  }}
                  disabled={mode === 'add'} // Disable when adding to existing
                />
                {formik.touched.due_date && formik.errors.due_date && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.due_date}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-7'>
              <div className='col-lg-6 fv-row'>
                <label className='required fw-bold fs-6 mb-2'>Payment Terms</label>
                <Select
                  styles={{
                    ...selectStyles,
                    control: (provided) => ({
                      ...selectStyles.control?.(provided),
                      backgroundColor: mode === 'add' ? '#d1f2eb' : provided.backgroundColor,
                    })
                  }}
                  options={paymentTermsOptions}
                  isSearchable={false}
                  placeholder='Select payment terms...'
                  onChange={handlePaymentTermsChange}
                  value={selectedPaymentTerms}
                  onBlur={() => formik.setFieldTouched('payment_terms', true)}
                  isDisabled={mode === 'add'} // Disable when adding to existing
                />
                {formik.touched.payment_terms && formik.errors.payment_terms && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.payment_terms}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className='col-lg-6 fv-row'>
                <label className='fw-bold fs-6 mb-2'>Amount Paid ({CURRENCY_APP})</label>
                <input
                  {...formik.getFieldProps('amount_paid')}
                  type='number'
                  step='0.01'
                  min='0'
                  max={getFinalTotalAmount()}
                  className={clsx(
                    'form-control form-control-lg form-control-solid',
                    {
                      'is-invalid': formik.touched.amount_paid && formik.errors.amount_paid,
                    },
                    {
                      'is-valid': formik.touched.amount_paid && !formik.errors.amount_paid,
                    }
                  )}
                  style={{
                    backgroundColor: mode === 'add' ? '#d1f2eb' : undefined
                  }}
                  placeholder='0.00'
                  disabled={mode === 'add'} // Disable when adding to existing
                />
              </div>
            </div>

            {/* Notes */}
            <div className='fv-row mb-7'>
              <label className='fw-bold fs-6 mb-2'>Notes</label>
              <textarea
                {...formik.getFieldProps('notes')}
                className={clsx(
                  'form-control form-control-solid',
                  { 'is-invalid': formik.touched.notes && formik.errors.notes },
                  { 'is-valid': formik.touched.notes && !formik.errors.notes }
                )}
                style={{
                  backgroundColor: mode === 'add' ? '#d1f2eb' : undefined
                }}
                rows={3}
                placeholder='Additional notes or comments...'
                disabled={mode === 'add'} // Disable when adding to existing
              />
            </div>

            {/* Product Selection Section */}
            <div className='fv-row mb-7'>
              <label className='required fw-bold fs-6 mb-2'>Select Product</label>
              <Select
                options={products}
                styles={selectStyles}
                isDisabled={false}
                isLoading={allproductsandtypesloading}
                isClearable={true}
                isSearchable={true}
                placeholder='Select product...'
                value={selectedProduct}
                onChange={handleProductChange}
              />
            </div>

            {/* Quantity and Cost Input */}
            <div className='row mb-7'>
              <div className='col-lg-6 fv-row'>
                <label className='required fw-bold fs-6 mb-2'>
                  Quantity{' '}
                  {selectedProduct && selectedProduct.units_in_stock !== undefined && (
                    <span className='text-muted'>
                      ({addCommas(selectedProduct.units_in_stock)}{' '}
                      {selectedProduct.unit_name || 'units'} in stock)
                    </span>
                  )}
                </label>
                <div className='d-flex align-items-center'>
                  <input
                    type='number'
                    min='1'
                    step='1'
                    className='form-control form-control-lg form-control-solid'
                    placeholder='1'
                    value={quantity}
                    onChange={(e) => setQuantity(Number(e.target.value))}
                    disabled={!selectedProduct}
                  />
                </div>
              </div>

              <div className='col-lg-6 fv-row mt-1'>
                <div className='d-flex justify-content-between align-items-baseline'>
                  <label className='fw-bold fs-6 mb-2'>Unit Cost ({CURRENCY_APP})</label>
                  {selectedProduct && !showUpdatePricing && (
                    <span
                      className='text-primary fs-8 cursor-pointer border border-primary rounded px-2 py-1 mx-2'
                      onClick={handleShowUpdatePricing}
                    >
                      update pricing
                    </span>
                  )}
                </div>
                <div className='d-flex align-items-center'>
                  <input
                    type='number'
                    min='0'
                    step='0.01'
                    className='form-control form-control-lg form-control-solid'
                    placeholder='0.00'
                    value={unitCost}
                    onChange={(e) => setUnitCost(Number(e.target.value))}
                    disabled={!selectedProduct}
                  />
                </div>
              </div>
            </div>

            {/* Update Pricing Section */}
            {showUpdatePricing && selectedProduct && (
              <>
                <div className='row mb-7'>
                  <div className='col-lg-6 fv-row'>
                    <label className='fw-bold fs-6 mb-2'>Buying Price ({CURRENCY_APP})</label>
                    <input
                      type='number'
                      min='0'
                      step='0.01'
                      className='form-control form-control-lg form-control-solid'
                      value={newBuyingPrice}
                      onChange={(e) => setNewBuyingPrice(Number(e.target.value))}
                    />
                  </div>
                  <div className='col-lg-6 fv-row mt-1'>
                    <label className='fw-bold fs-6 mb-2'>Selling Price ({CURRENCY_APP})</label>
                    <input
                      type='number'
                      min='0'
                      step='0.01'
                      className='form-control form-control-lg form-control-solid'
                      value={newSellingPrice}
                      onChange={(e) => setNewSellingPrice(Number(e.target.value))}
                    />
                  </div>
                </div>
                <div className='fv-row mb-7'>
                  <div className='d-flex justify-content-end'>
                    <button
                      type='button'
                      className='btn btn-light me-3'
                      onClick={() => {
                        setShowUpdatePricing(false)
                        setNewBuyingPrice(selectedProduct.unit_buying_price)
                        setNewSellingPrice(selectedProduct.unit_selling_price)
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary'
                      onClick={handleUpdatePrices}
                      disabled={loadingUpdate || newBuyingPrice < 0 || newSellingPrice < 0}
                    >
                      {loadingUpdate ? (
                        <span className='indicator-progress' style={{ display: 'block' }}>
                          Please wait...
                          <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                      ) : (
                        'Update'
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Total Cost Display */}
            {selectedProduct && quantity > 0 && (
              <div className='fv-row mb-7'>
                <div className='border border-gray-300 border-dashed rounded py-3 px-4'>
                  <div className='d-flex justify-content-between align-items-center'>
                    <span className='fw-bold fs-6 text-gray-600'>Item Total:</span>
                    <span className='fw-bolder fs-4 text-primary'>
                      {addCommas((quantity * unitCost).toFixed(2))} {CURRENCY_APP}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Add Item Buttons */}
            <div className='fv-row mb-7'>
              <div className='d-flex flex-column flex-grow-1'>
                <div className='d-flex flex-wrap justify-content-end'>
                  <button
                    type='button'
                    className='btn btn-light-danger me-2 mb-2'
                    disabled={purchaseItems.length === 0}
                    onClick={handleRemoveAllItems}
                  >
                    Remove All Items
                  </button>
                  <button
                    type='button'
                    className='btn btn-primary mb-2'
                    onClick={handleAddItem}
                    disabled={!selectedProduct || quantity <= 0}
                  >
                    Add Item
                  </button>
                </div>
              </div>
            </div>

            {/* Added Items Table */}
            <div className='fv-row mb-7'>
              <div className='card bg-light'>
                <div className='card-header border-0 pt-5'>
                  <h3 className='card-title align-items-start flex-column'>
                    <span className='required card-label fw-bolder fs-3 mb-1'>Purchase Items</span>
                    <span className='text-muted mt-1 fw-bold fs-7'>
                      {purchaseItems.length === 1
                        ? `${purchaseItems.length} item added`
                        : `${purchaseItems.length} items added`}
                    </span>
                  </h3>
                </div>
                <div className='card-body py-3'>
                  <div className='table-responsive'>
                    <Mainsubtable2
                      columnaddedtitems={onlyWidth > 500 ? itemColumns : itemColumnsMobile}
                      adddatatable={tableData}
                    />
                  </div>

                  {purchaseItems.length > 0 && (
                    <div className='fv-row mt-5 mb-5'>
                      <div className='d-flex flex-column flex-grow-1'>
                        <div className='d-flex justify-content-end'>
                          <div>
                            {mode === 'add' && existingTotalAmount > 0 && (
                              <>
                                <div className='d-flex justify-content-between mb-2'>
                                  <span className='fw-bold fs-7 text-gray-600'>Existing Total:</span>
                                  <span className='fw-bold fs-7' style={{ backgroundColor: '#d1f2eb', padding: '4px 8px', borderRadius: '4px' }}>
                                    {addCommas(existingTotalAmount.toFixed(2))} {CURRENCY_APP}
                                  </span>
                                </div>
                                <div className='d-flex justify-content-between mb-2'>
                                  <span className='fw-bold fs-7 text-gray-600'>New Items Total:</span>
                                  <span className='fw-bold fs-7'>
                                    {addCommas(totalAmount.toFixed(2))} {CURRENCY_APP}
                                  </span>
                                </div>
                              </>
                            )}
                            <div className='border-top border-bottom border-dark py-3'>
                              <span className='fs-5 fw-bolder me-2 mt-3'>
                                <span className='me-3'>Total Amount:</span>
                                <span className='text-primary' style={mode === 'add' ? { backgroundColor: '#d1f2eb', padding: '4px 8px', borderRadius: '4px' } : {}}>
                                  {addCommas(getFinalTotalAmount().toFixed(2))} {CURRENCY_APP}
                                </span>
                              </span>
                            </div>
                            {formik.values.amount_paid > 0 && (
                              <div className='pt-3'>
                                <div className='d-flex justify-content-between mb-2'>
                                  <span className='fw-bold fs-7 text-gray-600'>Amount Paid:</span>
                                  <span className='fw-bold fs-7' style={mode === 'add' ? { backgroundColor: '#d1f2eb', padding: '4px 8px', borderRadius: '4px' } : {}}>
                                    {addCommas(Number(formik.values.amount_paid).toFixed(2))}{' '}
                                    {CURRENCY_APP}
                                  </span>
                                </div>
                                <div className='d-flex justify-content-between'>
                                  <span className='fw-bold fs-7 text-gray-600'>Balance Due:</span>
                                  <span className='fw-bold fs-7 text-warning' style={mode === 'add' ? { backgroundColor: '#d1f2eb', padding: '4px 8px', borderRadius: '4px' } : {}}>
                                    {addCommas(
                                      (getFinalTotalAmount() - Number(formik.values.amount_paid)).toFixed(2)
                                    )}{' '}
                                    {CURRENCY_APP}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className='modal-footer border-0 mt-n5'>
            <button type='button' className='btn btn-light-primary' onClick={handleClose}>
              Cancel
            </button>
            <button
              type='submit'
              className='btn btn-success'
              disabled={
                loading ||
                formik.isSubmitting ||
                purchaseItems.length === 0 ||
                !formik.isValid
              }
            >
              {loading ? (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              ) : (
                <span className='indicator-label'>{mode === 'add' ? 'Add Items' : 'Save Purchase'}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  )
})
